import * as React from 'react';
import { UIHeading2, UIText1, Overline, UIHeading4, LinkButton, Avatar, Image } from '@cian/ui-kit';
import classNames from 'classnames';
import { MODERATION_STATUS_TYPES, NEW_OBJECTS_SET_KEYS } from '../../interfaces/villages';
import { IVillageForListPresenterSchema } from '../../repositories/countryside-search/entities/village/VillageForListPresenterSchema';
import { getVillageOffersType } from '../../utils/getVillageOffersType';
import { PriceDisclaimer } from '../PriceDisclaimer';
import { VillageObject } from '../village_object';
import { generateObjectsLink, getHighway, getStation, getVillageInfo } from './helpers';
import { trackCardOpened, trackListingObjectsOpened } from './tracking';
import { LinkAddresses } from './LinkAddresses';
import { showSerpLink } from './showSerpLink';
import * as styles from './index.css';
import { IGetVillageListRequestSchema } from '../../../node/repositories/countryside-search/entities/schemas/GetVillageListRequestSchema';
import { GalleryContainer } from '../../containers/GalleryContainer';
import { PhoneButtonContainer } from '../../containers/PhoneButtonContainer';
import { Link } from '../Link';
import { LabelsList } from '../LabelsList';
import { getPromotionLabels } from '../../utils';
import { getMicrodataAttributes } from '../../utils/seo';

export interface IVillageListItemOwnProps {
  filters: IGetVillageListRequestSchema;
  regionHasRoads: boolean;
  regionHasStations: boolean;
  village: IVillageForListPresenterSchema;
  serpLink: string;
}

export interface IVillageListItemDispatchProps {
  trackPhoneOpened(village: IVillageForListPresenterSchema): void;
  trackBuilderLinkCLick(village: IVillageForListPresenterSchema): void;
}

export type TVillageListItemProps = IVillageListItemOwnProps & IVillageListItemDispatchProps;

export class VillageListItem extends React.Component<TVillageListItemProps> {
  public render() {
    const { village, regionHasStations, regionHasRoads, filters } = this.props;

    const {
      builder,
      builderCounterSet,
      resaleCounterSet,
      geo,
      name,
      newobjectCategorySet,
      seo,
      builderOffersSeo,
      stationInfoList,
      promotions,
    } = village;

    const builderAllKpUrl = builder?.seo?.all?.url;

    const isBuilderRendered = builder.moderationStatus === MODERATION_STATUS_TYPES.Published;

    const showBuilderSerpLink = showSerpLink(builderOffersSeo, builderCounterSet);

    const disclaimerType = getVillageOffersType({
      builderCounterSet,
      resaleCounterSet,
    });

    const labels = getPromotionLabels(promotions);

    return (
      <div className={styles['item']}>
        <div className={styles['media']} {...getMicrodataAttributes('ImageObject')}>
          <div onClick={this.handleVillageCardClick} className={styles['media']}>
            <GalleryContainer images={village.thumbnailList} videos={village.videoList} />
          </div>
        </div>
        <div className={styles['main']}>
          <div className={styles['info-container']}>
            <div className={styles['info']}>
              {seo?.url ? (
                <a
                  className={styles['village-link']}
                  href={seo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Открыть коттеджный посёлок"
                  onClick={() => trackCardOpened(village)}
                >
                  <UIHeading2 color="primary_100" data-name="CardVillageName">
                    {name}
                  </UIHeading2>
                </a>
              ) : (
                <UIHeading2 data-name="CardVillageName">{name}</UIHeading2>
              )}
              <div className={styles['village-info']}>
                <UIText1 display="inline">{getVillageInfo(village)}</UIText1>
                {!!disclaimerType && <PriceDisclaimer type={disclaimerType} />}
              </div>
              {labels && (
                <div className={styles['labels']}>
                  <LabelsList labels={labels} />
                </div>
              )}
              <div>
                {getHighway(regionHasRoads, filters, village)}
                {getStation(regionHasStations, filters, stationInfoList)}
              </div>
              {geo?.address?.details && (
                <div className={styles['address']} {...getMicrodataAttributes('Place')}>
                  <span itemProp="address">
                    <LinkAddresses addressDetails={geo.address.details} />
                  </span>
                </div>
              )}
              <div className={styles['village_objects']}>
                {Object.values(NEW_OBJECTS_SET_KEYS).map(key => {
                  const object = newobjectCategorySet && newobjectCategorySet[key];

                  return (
                    !!object && (
                      <VillageObject
                        key={key}
                        object={object}
                        objectType={key}
                        villageId={village.id}
                        hidePrice={!!village.isCropped}
                        href={generateObjectsLink(key, object.seo, village.counterSet)}
                      />
                    )
                  );
                })}
              </div>
            </div>
            {isBuilderRendered && (
              <div className={styles['builder']}>
                {builder.logoUrl ? (
                  <div className={styles['builder-image']}>
                    <Image display="block" objectFit="contain" src={builder.logoUrl} />
                  </div>
                ) : (
                  <Avatar type="builder" size={64} />
                )}
                <div className={styles['builder-description']}>
                  <Overline color="gray60_100">Застройщик</Overline>
                  <UIHeading4>
                    {builderAllKpUrl ? (
                      <Link
                        href={builderAllKpUrl}
                        target="_blank"
                        aria-label="Открыть объекты застройщика"
                        onClick={this.onBuilderLinkCLick}
                      >
                        <span className={classNames(styles['builder-name'], styles['builder-name-link'])}>
                          {builder.name}
                        </span>
                      </Link>
                    ) : (
                      <span className={styles['builder-name']}>{builder.name}</span>
                    )}
                  </UIHeading4>
                </div>
                {builder.displayPhone && !!builder.phone && (
                  <PhoneButtonContainer phone={builder.phone} onShow={this.onClickPhone} />
                )}
                {showBuilderSerpLink && builderOffersSeo?.url && (
                  <div className={styles['objects-link']}>
                    <LinkButton
                      fullWidth
                      theme="fill_secondary"
                      size="XS"
                      href={builderOffersSeo.url}
                      target="_blank"
                      aria-label="Открыть объекты в коттеджном посёлке"
                      onClick={() => trackListingObjectsOpened(village)}
                    >
                      Объекты от застройщика
                    </LinkButton>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  private handleVillageCardClick = () => {
    const { village } = this.props;

    if (village.seo?.url) {
      trackCardOpened(village);
      window.open(village.seo.url, '_blank');
    }
  };

  private onClickPhone = () => {
    this.props.trackPhoneOpened(this.props.village);
  };

  private onBuilderLinkCLick = () => {
    this.props.trackBuilderLinkCLick(this.props.village);
  };
}
